import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createStaticVNode as _createStaticVNode } from "vue"
import _imports_0 from '@/assets/img/ring-placeholder.svg'


const _hoisted_1 = {
  key: 0,
  class: "relative w-full aspect-square overflow-hidden"
}
const _hoisted_2 = ["alt", "src"]
const _hoisted_3 = ["alt"]
const _hoisted_4 = {
  key: 1,
  class: "relative flex items-center justify-center h-full w-full"
}
const _hoisted_5 = {
  key: 0,
  class: "w-full"
}
const _hoisted_6 = {
  class: "mx-auto w-full relative z-30",
  autoplay: "",
  loop: "",
  muted: "",
  playsinline: ""
}
const _hoisted_7 = ["src"]
const _hoisted_8 = ["alt", "src"]
const _hoisted_9 = ["src"]
const _hoisted_10 = ["src"]
const _hoisted_11 = {
  key: 2,
  class: "relative top-0 left-0"
}

import {computed, onBeforeUnmount, onMounted, ref, watch} from "vue";

const maxRetries = 10;
const pageStr = "components.itemImage"


export default /*@__PURE__*/_defineComponent({
  __name: 'itemImage',
  props: {
    imageUrl: { default: "" },
    imageLayerUrl: { default: "" },
    mainMetal: { default: "" },
    zoom: { type: Boolean, default: false },
    focusMode: { type: Boolean, default: false },
    focusModeWidth: { default: "" },
    focusModeIdx: { default: "" },
    isWeddingRing: { type: Boolean, default: false }
  },
  setup(__props: any) {

const props = __props;

const finaleImageUrl = ref("");
const finaleImageLayerUrl = ref("");
const isUnmounted = ref(false);
const isVisible = ref(false);
const containerRef = ref<HTMLElement | null>(null);
let intersectionObserver: IntersectionObserver | null = null;

onMounted(() => {
  intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isVisible.value) {
          isVisible.value = true;
          loadImage(props.imageUrl);
          loadLayerImage(props.imageLayerUrl);
          intersectionObserver?.disconnect();
        }
      },
      {threshold: 0.01}
  );
  if (containerRef.value) {
    intersectionObserver.observe(containerRef.value);
  }
});

onBeforeUnmount(() => {
  isUnmounted.value = true;
  intersectionObserver?.disconnect();
});

const isZooming = ref(false);
const zoomX = ref(0);
const zoomY = ref(0);

const handleMouseDown = (event: MouseEvent) => {
  if (isZoomAllowed.value && event.button === 0) {
    event.preventDefault();
    isZooming.value = true;
    updateZoomPosition(event);
  }
};

const handleMouseMove = (event: MouseEvent) => {
  if (isZooming.value) {
    updateZoomPosition(event);
  }
};

const handleMouseUp = () => {
  isZooming.value = false;
};

const handleMouseLeave = () => {
  isZooming.value = false;
};

const updateZoomPosition = (event: MouseEvent) => {
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
  const x = ((event.clientX - rect.left) / rect.width) * 100;
  const y = ((event.clientY - rect.top) / rect.height) * 100;
  zoomX.value = x;
  zoomY.value = y;
};

const zoomStyle = computed(() => {
  if (!isZooming.value) {
    return {};
  }
  return {
    transform: `scale(3.5)`,
    transformOrigin: `${zoomX.value}% ${zoomY.value}%`,
  };
});

const retryCounter = ref(0);
const retryCounterLayer = ref(0);
const isOldImage = ref(false);
const isZoomAllowed = computed(() => {
  return !(!props.zoom || props.focusMode ||
      props.imageUrl == "" ||
      props.imageUrl.includes('closeup-') ||
      props.imageUrl.includes('model-') ||
      props.imageUrl.includes('still-'));
})

/**
 * Image position
 */

const image_pos = computed(() => {
  if (props.imageUrl.includes("/pos")) {
    const imageUrlParts = props.imageUrl.split("/");

    return imageUrlParts[imageUrlParts.length - 2];
  }

  return "pos1";
});

/**
 * Das Bild wird so lange neu geladen, bis es vom Server komplett generiert wurde.
 * @param imageUrl
 */
const loadImage = (imageUrl: string) => {
  if (imageUrl.includes("mp4")) {
    finaleImageUrl.value = imageUrl;
    retryCounter.value = 0;
  } else {
    // console.debug("Try to load", imageUrl);
    retryCounter.value += 1;
    const img = new Image();
    img.src = imageUrl;

    img.onload = () => {
      if (isUnmounted.value) return;
      finaleImageUrl.value = imageUrl;
      retryCounter.value = 0;
      isOldImage.value = img.naturalWidth == 600;
    };
    img.onerror = () => {
      if (isUnmounted.value) return;
      if (retryCounter.value <= maxRetries) {
        setTimeout(loadImage, 100, imageUrl);
      }
    };
  }
};

const loadLayerImage = (imageLayerUrl: string) => {
  if (imageLayerUrl != "" && typeof imageLayerUrl != "undefined") {
    // console.debug("Try to load", imageLayerUrl);
    retryCounterLayer.value += 1;
    const img = new Image();
    img.src = imageLayerUrl;

    img.onload = () => {
      if (isUnmounted.value) return;
      finaleImageLayerUrl.value = imageLayerUrl;
    };
    img.onerror = () => {
      if (isUnmounted.value) return;
      if (retryCounterLayer.value <= maxRetries) {
        setTimeout(loadImage, 600, imageLayerUrl);
      }
    };
  } else {
    finaleImageLayerUrl.value = "";
  }
};

/**
 * Watcher überwacht, ob sich das Bild verändert hat, und startet den Lade-Prozess erneut
 */
watch(
    () => props.imageUrl,
    (imageUrl: string) => {
      if (isVisible.value) {
        loadImage(imageUrl);
      }
    },
    {deep: true},
);

watch(
    () => props.imageLayerUrl,
    (imageUrl: string) => {
      if (isVisible.value) {
        loadLayerImage(imageUrl);
      }
    },
    {deep: true},
);

/**
 * Focus mode
 */

const focusModeParameter: Record<string, Record<string, string>> = {
  // ok
  "default": {
    "2.0": "max-w-none w-72 h-72 object-cover -top-38 left-18",
    "2.2": "max-w-none w-71 h-71 object-cover -top-38 left-18",
    "2.5": "max-w-none w-70 h-70 object-cover -top-36 left-17",
    "2.7": "max-w-none w-68 h-68 object-cover -top-34 left-16",
    "3.0": "max-w-none w-66 h-66 object-cover -top-32 left-16",
    "3.5": "max-w-none w-64 h-64 object-cover -top-30 left-15",
    "4.0": "max-w-none w-61 h-61 object-cover -top-29 left-14",
    "4.5": "max-w-none w-59 h-59 object-cover -top-27 left-14",
    "5.0": "max-w-none w-57 h-57 object-cover -top-26 left-13",
    "5.5": "max-w-none w-54 h-54 object-cover -top-24 left-12",
    "6.0": "max-w-none w-52 h-52 object-cover -top-22 left-12",
    "6.5": "max-w-none w-49 h-49 object-cover -top-20 left-11",
    "7.0": "max-w-none w-46 h-46 object-cover -top-18 left-11",
    "7.5": "max-w-none w-43 h-43 object-cover -top-16 left-10",
    "8.0": "max-w-none w-40 h-40 object-cover -top-15 left-8",
    "8.5": "max-w-none w-37 h-37 object-cover -top-13 left-7",
    "9.0": "max-w-none w-34 h-34 object-cover -top-11 left-6",
    "9.5": "max-w-none w-31 h-31 object-cover -top-9 left-5",
    "10.0": "max-w-none w-28 h-28 object-cover -top-7 left-4",
    "11.0": "max-w-none w-24 h-24 object-cover -top-5 left-3",
    "12.0": "max-w-none w-20 h-20 object-cover -top-3 left-2",
  },
  "profile": {
    "2.0": "w-36 h-36 object-cover -top-12 left-0",
    "2.2": "w-36 h-36 object-cover -top-12 left-0",
    "2.5": "w-36 h-36 object-cover -top-12 left-0",
    "2.7": "w-36 h-36 object-cover -top-12 left-0",
    "3.0": "w-36 h-36 object-cover -top-12 left-0",
    "3.5": "w-36 h-36 object-cover -top-12 left-0",
    "4.0": "w-36 h-36 object-cover -top-12 left-0",
    "4.5": "w-36 h-36 object-cover -top-12 left-0",
    "5.0": "w-36 h-36 object-cover -top-12 left-0",
    "5.5": "w-36 h-36 object-cover -top-12 left-0",
    "6.0": "w-46 h-46 object-cover -top-14 left-0",
    "6.5": "w-36 h-36 object-cover -top-12 left-0",
    "7.0": "w-36 h-36 object-cover -top-12 left-0",
    "7.5": "w-36 h-36 object-cover -top-12 left-0",
    "8.0": "w-36 h-36 object-cover -top-12 left-0",
    "8.5": "w-36 h-36 object-cover -top-12 left-0",
    "9.0": "w-36 h-36 object-cover -top-12 left-0",
    "9.5": "w-36 h-36 object-cover -top-12 left-0",
    "10.0": "w-36 h-36 object-cover -top-12 left-0",
    "11.0": "w-36 h-36 object-cover -top-12 left-0",
    "12.0": "w-36 h-36 object-cover -top-12 left-0",
  },
  // ok
  "width": {
    "2.0": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "2.2": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "2.5": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "2.7": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "3.0": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "3.5": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "4.0": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "4.5": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "5.0": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "5.5": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "6.0": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "6.5": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "7.0": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "7.5": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "8.0": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "8.5": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "9.0": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "9.5": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "10.0": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "11.0": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "12.0": "max-w-none w-40 h-40 object-cover -top-24 left-12",
  },
  "grooves.*.groove_type": {
    "2.0": "max-w-none w-50 h-50 object-cover -top-26 left-12",
    "2.2": "max-w-none w-49 h-49 object-cover -top-25 left-12",
    "2.5": "max-w-none w-49 h-49 object-cover -top-25 left-12",
    "2.7": "max-w-none w-48 h-48 object-cover -top-24 left-12",
    "3.0": "max-w-none w-47 h-47 object-cover -top-23 left-12",
    "3.5": "max-w-none w-46 h-46 object-cover -top-22 left-11",
    "4.0": "max-w-none w-44 h-44 object-cover -top-21 left-11",
    "4.5": "max-w-none w-43 h-43 object-cover -top-20 left-11",
    "5.0": "max-w-none w-42 h-42 object-cover -top-19 left-10",
    "5.5": "max-w-none w-40 h-40 object-cover -top-18 left-10",
    "6.0": "max-w-none w-40 h-40 object-cover -top-18 left-10",
    "6.5": "max-w-none w-39 h-39 object-cover -top-17 left-9",
    "7.0": "max-w-none w-38 h-38 object-cover -top-16 left-9",
    "7.5": "max-w-none w-36 h-36 object-cover -top-15 left-8",
    "8.0": "max-w-none w-34 h-34 object-cover -top-14 left-8",
    "8.5": "max-w-none w-33 h-33 object-cover -top-13 left-7",
    "9.0": "max-w-none w-32 h-32 object-cover -top-12 left-7",
    "9.5": "max-w-none w-30 h-30 object-cover -top-11 left-6",
    "10.0": "max-w-none w-29 h-29 object-cover -top-10 left-6",
    "11.0": "max-w-none w-28 h-28 object-cover -top-9 left-5",
    "12.0": "max-w-none w-28 h-28 object-cover -top-8 left-5",
  },
  // ok
  "grooves.*.width": {
    "2.0": "max-w-none w-50 h-50 object-cover -top-26 left-12",
    "2.2": "max-w-none w-49 h-49 object-cover -top-25 left-12",
    "2.5": "max-w-none w-49 h-49 object-cover -top-25 left-12",
    "2.7": "max-w-none w-48 h-48 object-cover -top-24 left-12",
    "3.0": "max-w-none w-47 h-47 object-cover -top-23 left-12",
    "3.5": "max-w-none w-46 h-46 object-cover -top-22 left-11",
    "4.0": "max-w-none w-44 h-44 object-cover -top-21 left-11",
    "4.5": "max-w-none w-43 h-43 object-cover -top-20 left-11",
    "5.0": "max-w-none w-42 h-42 object-cover -top-19 left-10",
    "5.5": "max-w-none w-40 h-40 object-cover -top-18 left-10",
    "6.0": "max-w-none w-40 h-40 object-cover -top-18 left-10",
    "6.5": "max-w-none w-39 h-39 object-cover -top-17 left-9",
    "7.0": "max-w-none w-38 h-38 object-cover -top-16 left-9",
    "7.5": "max-w-none w-36 h-36 object-cover -top-15 left-8",
    "8.0": "max-w-none w-34 h-34 object-cover -top-14 left-8",
    "8.5": "max-w-none w-33 h-33 object-cover -top-13 left-7",
    "9.0": "max-w-none w-32 h-32 object-cover -top-12 left-7",
    "9.5": "max-w-none w-30 h-30 object-cover -top-11 left-6",
    "10.0": "max-w-none w-29 h-29 object-cover -top-10 left-6",
    "11.0": "max-w-none w-28 h-28 object-cover -top-9 left-5",
    "12.0": "max-w-none w-28 h-28 object-cover -top-8 left-5",
  },
  "stones.stone_presets": {
    "2.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "2.2": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "2.5": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "2.7": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "3.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "3.5": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "4.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "4.5": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "5.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "5.5": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "6.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "6.5": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "7.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "7.5": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "8.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "8.5": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "9.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "9.5": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "10.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "11.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "12.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
  },
  "stones.*.stone_size": {
    "2.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "2.2": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "2.5": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "2.7": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "3.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "3.5": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "4.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "4.5": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "5.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "5.5": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "6.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "6.5": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "7.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "7.5": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "8.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "8.5": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "9.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "9.5": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "10.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "11.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "12.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
  },
  "discs.*.color": {
    "2.0": "max-w-none w-50 h-50 object-cover -top-26 left-12",
    "2.2": "max-w-none w-49 h-49 object-cover -top-25 left-12",
    "2.5": "max-w-none w-49 h-49 object-cover -top-25 left-12",
    "2.7": "max-w-none w-48 h-48 object-cover -top-24 left-12",
    "3.0": "max-w-none w-47 h-47 object-cover -top-23 left-12",
    "3.5": "max-w-none w-46 h-46 object-cover -top-22 left-11",
    "4.0": "max-w-none w-44 h-44 object-cover -top-21 left-11",
    "4.5": "max-w-none w-43 h-43 object-cover -top-20 left-11",
    "5.0": "max-w-none w-42 h-42 object-cover -top-19 left-10",
    "5.5": "max-w-none w-40 h-40 object-cover -top-18 left-10",
    "6.0": "max-w-none w-40 h-40 object-cover -top-18 left-10",
    "6.5": "max-w-none w-39 h-39 object-cover -top-17 left-9",
    "7.0": "max-w-none w-38 h-38 object-cover -top-16 left-9",
    "7.5": "max-w-none w-36 h-36 object-cover -top-15 left-8",
    "8.0": "max-w-none w-34 h-34 object-cover -top-14 left-8",
    "8.5": "max-w-none w-33 h-33 object-cover -top-13 left-7",
    "9.0": "max-w-none w-32 h-32 object-cover -top-12 left-7",
    "9.5": "max-w-none w-30 h-30 object-cover -top-11 left-6",
    "10.0": "max-w-none w-29 h-29 object-cover -top-10 left-6",
    "11.0": "max-w-none w-28 h-28 object-cover -top-9 left-5",
    "12.0": "max-w-none w-28 h-28 object-cover -top-8 left-5",
  },
  "discs.*.finish": {
    "2.0": "max-w-none w-50 h-50 object-cover -top-26 left-12",
    "2.2": "max-w-none w-49 h-49 object-cover -top-25 left-12",
    "2.5": "max-w-none w-49 h-49 object-cover -top-25 left-12",
    "2.7": "max-w-none w-48 h-48 object-cover -top-24 left-12",
    "3.0": "max-w-none w-47 h-47 object-cover -top-23 left-12",
    "3.5": "max-w-none w-46 h-46 object-cover -top-22 left-11",
    "4.0": "max-w-none w-44 h-44 object-cover -top-21 left-11",
    "4.5": "max-w-none w-43 h-43 object-cover -top-20 left-11",
    "5.0": "max-w-none w-42 h-42 object-cover -top-19 left-10",
    "5.5": "max-w-none w-40 h-40 object-cover -top-18 left-10",
    "6.0": "max-w-none w-40 h-40 object-cover -top-18 left-10",
    "6.5": "max-w-none w-39 h-39 object-cover -top-17 left-9",
    "7.0": "max-w-none w-38 h-38 object-cover -top-16 left-9",
    "7.5": "max-w-none w-36 h-36 object-cover -top-15 left-8",
    "8.0": "max-w-none w-34 h-34 object-cover -top-14 left-8",
    "8.5": "max-w-none w-33 h-33 object-cover -top-13 left-7",
    "9.0": "max-w-none w-32 h-32 object-cover -top-12 left-7",
    "9.5": "max-w-none w-30 h-30 object-cover -top-11 left-6",
    "10.0": "max-w-none w-29 h-29 object-cover -top-10 left-6",
    "11.0": "max-w-none w-28 h-28 object-cover -top-9 left-5",
    "12.0": "max-w-none w-28 h-28 object-cover -top-8 left-5",
  },
}

const getFocusModeCssClasses = () => {
  if (props.focusMode) {
    const widthStr = Number(props.focusModeWidth).toFixed(1).toString()
    let idxKey = props.focusModeIdx ? props.focusModeIdx.toString().trim() : ""



    // spilt idxKey by ".", if the second part is a number, replace it with the char "*" and join the idxKey again by "."
    const idxKeySplitted = idxKey.split(".")
    if (idxKeySplitted.length > 1 && !isNaN(idxKeySplitted[1])) {
      idxKeySplitted[1] = "*"
      idxKey = idxKeySplitted.join(".")
    }

    console.log("-" + idxKey + "-", widthStr, typeof focusModeParameter[idxKey], typeof focusModeParameter[idxKey][widthStr])

    if (
        idxKey !== "" &&
        typeof focusModeParameter[idxKey] !== "undefined" &&
        typeof focusModeParameter[idxKey][widthStr] === "string"
    ) {
      return focusModeParameter[idxKey][widthStr]
    } else if (typeof focusModeParameter.default[widthStr] === "string") {
      return focusModeParameter.default[widthStr]
    }

    return focusModeParameter.default["5.0"]
  } else {
    return "w-full"
  }
}


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "containerRef",
    ref: containerRef
  }, [
    (retryCounter.value > maxRetries && !__props.imageUrl.includes('.mp4'))
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          (typeof __props.mainMetal != 'undefined' && __props.mainMetal != '')
            ? (_openBlock(), _createElementBlock("img", {
                key: 0,
                alt: _ctx.$t(pageStr + '.main_metal_background'),
                src: 
            'https://rubin-website-images.s3.eu-central-1.amazonaws.com/assets/backgrounds/' +
            __props.mainMetal +
            '_' +
            image_pos.value +
            '.webp'
          ,
                class: "absolute inset-0 w-full h-full object-cover"
              }, null, 8, _hoisted_2))
            : _createCommentVNode("", true),
          _createElementVNode("img", {
            src: _imports_0,
            alt: _ctx.$t(pageStr + '.error_unable_to_load_image'),
            class: "relative w-full h-full object-contain",
            width: "600",
            height: "600"
          }, null, 8, _hoisted_3)
        ]))
      : (finaleImageUrl.value != '')
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            (finaleImageUrl.value.includes('.mp4'))
              ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                  _createElementVNode("video", _hoisted_6, [
                    _createElementVNode("source", {
                      src: finaleImageUrl.value,
                      type: "video/mp4"
                    }, null, 8, _hoisted_7),
                    _createTextVNode(" " + _toDisplayString(_ctx.$t(pageStr + '.video_not_supported')), 1)
                  ])
                ]))
              : (_openBlock(), _createElementBlock("div", {
                  key: 1,
                  class: _normalizeClass(["relative w-full h-full overflow-hidden flex items-center justify-center", {'cursor-zoom-in': isZoomAllowed.value}]),
                  onMousedown: handleMouseDown,
                  onMousemove: handleMouseMove,
                  onMouseup: handleMouseUp,
                  onMouseleave: handleMouseLeave
                }, [
                  (typeof __props.mainMetal  != 'undefined' && __props.mainMetal != '' && isOldImage.value === false)
                    ? (_openBlock(), _createElementBlock("img", {
                        key: 0,
                        alt: _ctx.$t(pageStr + '.main_metal_background'),
                        src: 
            'https://rubin-website-images.s3.eu-central-1.amazonaws.com/assets/backgrounds/' +
            __props.mainMetal +
            '_' +
            image_pos.value +
            '.webp'
          ,
                        class: _normalizeClass(["z-10 absolute inset-0", getFocusModeCssClasses()]),
                        style: _normalizeStyle(zoomStyle.value)
                      }, null, 14, _hoisted_8))
                    : _createCommentVNode("", true),
                  (__props.isWeddingRing)
                    ? (_openBlock(), _createElementBlock("img", {
                        key: 1,
                        src: "https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/shadow/universal_shadow.v2.webp",
                        class: _normalizeClass(["z-10 absolute inset-0", getFocusModeCssClasses()]),
                        style: _normalizeStyle(zoomStyle.value)
                      }, null, 6))
                    : _createCommentVNode("", true),
                  _createElementVNode("img", {
                    src: finaleImageUrl.value,
                    class: _normalizeClass(["z-10 relative m-auto", getFocusModeCssClasses()]),
                    style: _normalizeStyle(zoomStyle.value)
                  }, null, 14, _hoisted_9),
                  (finaleImageLayerUrl.value != '')
                    ? (_openBlock(), _createElementBlock("img", {
                        key: 2,
                        src: finaleImageLayerUrl.value,
                        class: _normalizeClass(["absolute inset-0 mt-0 z-30 m-auto", getFocusModeCssClasses()]),
                        style: _normalizeStyle(zoomStyle.value)
                      }, null, 14, _hoisted_10))
                    : _createCommentVNode("", true)
                ], 34))
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_11, [...(_cache[0] || (_cache[0] = [
            _createStaticVNode("<div class=\"flex items-end justify-center h-64 pt-4 px-4 pb-20 text-center sm:block sm:p-0\" data-v-6632ab7e><div class=\"inset-0 bg-gray-100 bg-opacity-75 transition-opacity\" aria-hidden=\"true\" data-v-6632ab7e></div><span class=\"hidden sm:inline-block sm:align-middle\" aria-hidden=\"true\" data-v-6632ab7e>​</span><div class=\"inline-block align-middle overflow-hidden object-center transform transition-all sm:my-8 sm:align-middle sm:max-w-sm sm:w-full sm:p-6\" data-v-6632ab7e><div class=\"lds-ripple border-brand-main\" data-v-6632ab7e><div class=\"border-brand-main\" data-v-6632ab7e></div><div class=\"border-brand-main\" data-v-6632ab7e></div></div></div></div>", 1)
          ]))]))
  ], 512))
}
}

})